import Image from 'next/image';
import { cn } from '@medizzconnect/shared/utils';

interface HeroBannerProps {
  /** Additional CSS classes for the outer wrapper */
  className?: string;
  /** Show the logo overlay (default: true) */
  showLogo?: boolean;
  /** Number of doctor photos to display (default: 8, cycles through 1-4.jpg) */
  photoCount?: number;
}

const COLOR_BARS = [
  'bg-[#a8e6cf]', // mint
  'bg-[#4a8c7f]', // dark-teal
  'bg-[#5ba89d]', // teal
  'bg-[#7ec8b8]', // light-teal
  'bg-[#5bbfcf]', // cyan
  'bg-[#a0e4ec]', // light-cyan
] as const;

const DOCTOR_PHOTOS = [
  { src: '/images/dk/1.jpg', width: 1006, height: 996 },
  { src: '/images/dk/2.jpg', width: 1024, height: 1024 },
  { src: '/images/dk/3.jpg', width: 1024, height: 1024 },
  { src: '/images/dk/4.jpg', width: 1024, height: 1024 },
] as const;

/**
 * Reusable hero banner with color bars, doctor photos and optional logo.
 * Used on the pricing page, in email templates, and other marketing pages.
 *
 * Usage:
 *   <HeroBanner />
 *   <HeroBanner showLogo={false} photoCount={4} className="mb-12" />
 */
export function HeroBanner({
  className,
  showLogo = true,
  photoCount = 4,
}: HeroBannerProps) {
  // Use the original high-resolution doctor photos for a sharper result
  const photos = Array.from({ length: photoCount }, (_, i) => {
    const photo = DOCTOR_PHOTOS[i % DOCTOR_PHOTOS.length] ?? DOCTOR_PHOTOS[0];

    return {
      ...photo,
      key: i,
    };
  });

  return (
    <div className={cn('relative w-full', className)}>
      {/* Color Bars — constrained to content width (max-w-7xl) */}
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-20 sm:h-24 w-full">
          {COLOR_BARS.map((barClass, i) => (
            <div key={i} className={cn('flex-1', barClass)} />
          ))}
        </div>
      </div>

      {/* Doctor Photos — right-aligned within content width, overlapping the bars */}
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="relative flex justify-end -mt-10 sm:-mt-12">
          {photos.map((photo) => (
            <div
              key={photo.key}
              className="relative w-[100px] sm:w-[120px] aspect-[5/4] overflow-hidden"
            >
              <Image
                alt=""
                src={photo.src}
                width={photo.width}
                height={photo.height}
                sizes="(min-width: 640px) 120px, 100px"
                unoptimized
                className="h-full w-full object-cover object-top"
                priority
              />
            </div>
          ))}

          {/* Logo Overlay — over the photos with white background */}
          {showLogo && (
            <div className="absolute bottom-[-20px] right-0 z-10 bg-white rounded-tl-lg px-3 py-1.5 shadow-sm">
              <Image
                alt="medizzconnect™"
                src="/images/Medizzconnect_Logo_Black_tm.webp"
                width={220}
                height={66}
                className="w-[140px] sm:w-[200px] h-auto"
                priority
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Static HTML version for use in emails (no React/Next.js).
 * Returns a plain HTML string that can be embedded in email templates.
 *
 * @param baseUrl - The base URL for images (e.g. "https://medizzconnect.com")
 * @param photoCount - Number of doctor photos (default: 8)
 */
export function getHeroBannerHTML(baseUrl: string, photoCount = 8): string {
  const barColors = ['#a8e6cf', '#4a8c7f', '#5ba89d', '#7ec8b8', '#5bbfcf', '#a0e4ec'];

  const barsHTML = barColors
    .map(
      (color) =>
        `<td style="width:${(100 / barColors.length).toFixed(2)}%;height:80px;background-color:${color};"></td>`
    )
    .join('');

  const photos = Array.from({ length: photoCount }, (_, i) => (i % 4) + 1);
  const photosHTML = photos
    .map(
      (n) =>
        `<img alt="" width="80" height="100" src="${baseUrl}/images/dk/${n}.jpg" style="display:inline-block;width:80px;height:100px;object-fit:cover;object-position:top;" />`
    )
    .join('');

  return `
<div style="position:relative;width:100%;overflow:hidden;">
  <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0">
    <tr>${barsHTML}</tr>
  </table>
  <div style="text-align:right;margin-top:-40px;">
    ${photosHTML}
  </div>
  <div style="margin-top:8px;">
    <img alt="medizzconnect™" width="220" height="66" src="${baseUrl}/images/Medizzconnect_Logo_Black_tm.png" style="width:220px;height:auto;" />
  </div>
</div>`.trim();
}
